import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ConnectionStatus from "./components/ConnectionStatus";
import PWAInstallBanner from "./components/PWAInstallBanner";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AuthPage from "./pages/AuthPage";
import AdminPage from "./pages/AdminPage";
import HomePage from "./pages/HomePage";
import LectureDashboard from "./pages/LectureDashboard";
import GrammaireDashboard from "./pages/GrammaireDashboard";
import DashboardPage from "./pages/DashboardPage";
import TextEditorPage from "./pages/TextEditorPage";
import ContentEditorPage from "./pages/ContentEditorPage";
import ProfileManagerPage from "./pages/ProfileManagerPage";
import ReaderPage from "./pages/ReaderPage";
import StudentsPage from "./pages/StudentsPage";
import RewardsPage from "./pages/RewardsPage";
import ClassDashboardPage from "./pages/ClassDashboardPage";
import DictationPage from "./pages/DictationPage";
import GrammarPage from "./pages/GrammarPage";
import ParentProgressPage from "./pages/ParentProgressPage";
import MathPage from "./pages/MathPage";
import EnglishPage from "./pages/EnglishPage";
import StudentResourceViewerPage from "./pages/StudentResourceViewerPage";
import ParentLoginPage from "./pages/ParentLoginPage";
import ParentDashboardPage from "./pages/ParentDashboardPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-orange-50">
        <div className="text-center">
          <img
            src="/LEcoleDeNavi4.png"
            alt="Chargement..."
            className="h-24 mx-auto mascot-float"
          />
          <p className="font-fredoka text-xl text-slate-500 mt-4">
            Chargement...
          </p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return null;

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/"
        element={user ? <Navigate to="/home" replace /> : <LandingPage />}
      />
      <Route
        path="/login"
        element={user ? <Navigate to="/home" replace /> : <LoginPage />}
      />
      
      {/* Secure Auth (Email/Password) */}
      <Route path="/auth" element={<AuthPage />} />
      
      {/* Admin Panel */}
      <Route path="/admin" element={<AdminPage />} />
      
      {/* Main Hub - Module Selection */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />

      {/* Module: Lecture */}
      <Route
        path="/lecture"
        element={
          <ProtectedRoute>
            <LectureDashboard />
          </ProtectedRoute>
        }
      />

      {/* Module: Grammaire */}
      <Route
        path="/grammaire"
        element={
          <ProtectedRoute>
            <GrammaireDashboard />
          </ProtectedRoute>
        }
      />

      {/* Legacy Dashboard (redirect to home) */}
      <Route
        path="/dashboard"
        element={<Navigate to="/home" replace />}
      />

      {/* Text Editor */}
      <Route
        path="/editor"
        element={
          <ProtectedRoute>
            <TextEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editor/:textId"
        element={
          <ProtectedRoute>
            <TextEditorPage />
          </ProtectedRoute>
        }
      />

      {/* Content Editor (Drag & Drop) */}
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <ContentEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create/:resourceId"
        element={
          <ProtectedRoute>
            <ContentEditorPage />
          </ProtectedRoute>
        }
      />
      {/* Editor from session - query param ?session=xxx */}
      <Route
        path="/create/from-session"
        element={
          <ProtectedRoute>
            <ContentEditorPage />
          </ProtectedRoute>
        }
      />

      {/* Reader (immersive - no navbar) */}
      <Route
        path="/reader/:textId"
        element={
          <ProtectedRoute>
            <ReaderPage />
          </ProtectedRoute>
        }
      />

      {/* Profile Manager */}
      <Route
        path="/profiles"
        element={
          <ProtectedRoute>
            <ProfileManagerPage />
          </ProtectedRoute>
        }
      />

      {/* Students Management */}
      <Route
        path="/students"
        element={
          <ProtectedRoute>
            <StudentsPage />
          </ProtectedRoute>
        }
      />

      {/* Rewards */}
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <RewardsPage />
          </ProtectedRoute>
        }
      />

      {/* Class Dashboard */}
      <Route
        path="/class"
        element={
          <ProtectedRoute>
            <ClassDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Dictation Module */}
      <Route
        path="/dictation"
        element={
          <ProtectedRoute>
            <DictationPage />
          </ProtectedRoute>
        }
      />

      {/* Grammar Analysis */}
      <Route
        path="/grammar"
        element={
          <ProtectedRoute>
            <GrammarPage />
          </ProtectedRoute>
        }
      />

      {/* Parent Progress (Public) */}
      <Route
        path="/parent/:shareToken"
        element={<ParentProgressPage />}
      />

      {/* Parent Login with Family Code */}
      <Route
        path="/parent"
        element={<ParentLoginPage />}
      />

      {/* Parent Dashboard */}
      <Route
        path="/parent/dashboard"
        element={<ParentDashboardPage />}
      />

      {/* Math Module */}
      <Route
        path="/math"
        element={
          <ProtectedRoute>
            <MathPage />
          </ProtectedRoute>
        }
      />

      {/* English Module */}
      <Route
        path="/english"
        element={
          <ProtectedRoute>
            <EnglishPage />
          </ProtectedRoute>
        }
      />

      {/* Student Resource Viewer */}
      <Route
        path="/view/:variantId"
        element={
          <ProtectedRoute>
            <StudentResourceViewerPage />
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ConnectionStatus />
        <PWAInstallBanner />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
